/**
 * Shared building blocks for the Traffic area (overview, people, funnels).
 *
 * The channel palette lives here so every panel colours a channel the SAME
 * way — colour follows the entity, never its rank. The seven hues were
 * validated for colour-blind separation against the dark chart surface
 * (#18181b) in the order CHANNEL_ORDER renders them, so keep order and
 * assignment together if you ever change one.
 */

import type { ComponentType } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";

export interface Count {
  label: string;
  channel?: string;
  views: number;
}
export type Metric = "views" | "visitors";

export const COLOR_VISITORS = "#6366f1";
export const COLOR_VIEWS = "#c2410c";
// The chart "surface" color (donut slice gaps, map country borders) must
// match the card the chart sits on, which changes with the theme — read it
// from useChartColors().surface (src/lib/theme.ts) instead of a constant.

/** Fixed render order for channel slices/legends — this exact adjacency is
 *  what was validated for colour-vision-deficiency separation. */
export const CHANNEL_ORDER = [
  "Social",
  "Email",
  "Search",
  "Paid",
  "Direct",
  "Referral",
  "Campaign",
  "AI",
] as const;

export const CHANNEL_COLORS: Record<string, string> = {
  Social: "#3b82f6",
  Email: "#d97706",
  Search: "#059669",
  Paid: "#c026d3",
  Direct: "#0891b2",
  Referral: "#f43f5e",
  Campaign: "#8b5cf6",
  // ChatGPT / Perplexity / Copilot click-throughs — broken out from Search
  // by the platform because "AI sends us traffic" is its own story now.
  AI: "#65a30d",
};

export function channelColor(channel: string | undefined): string {
  // Fallback is a mid zinc that stays legible on both themes.
  return CHANNEL_COLORS[channel ?? ""] ?? "#71717a";
}

/** ISO 3166 alpha-2 → emoji flag. Pure codepoint math, no data table. */
export function countryFlag(code: string): string {
  const cc = (code ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  return String.fromCodePoint(
    ...[...cc].map((c) => 127397 + c.charCodeAt(0)),
  );
}

export function regionName(code: string): string {
  try {
    return new Intl.DisplayNames(undefined, { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** "1m 42s" — visit durations are short, so minutes+seconds reads best. */
export function fmtDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** "just now" / "4m ago" / "3h ago" / date — for last-seen columns. */
export function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** Percentage change vs the previous window. Null when there's no baseline —
 *  "+100%" against zero is noise, not information. */
export function delta(now: number, before: number): number | null {
  if (before <= 0) return null;
  return Math.round(((now - before) / before) * 100);
}

export function Stat({
  icon: Icon,
  color,
  label,
  value,
  change,
  hint,
}: {
  icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color?: string;
  label: string;
  value: string;
  change?: number | null;
  hint?: string;
}) {
  const up = change != null && change > 0;
  const down = change != null && change < 0;
  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900 p-4">
      <div className="flex items-center gap-2">
        <span
          className="flex h-6 w-6 items-center justify-center rounded-md"
          style={{ backgroundColor: `${color ?? "#71717a"}22` }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color: color ?? "#71717a" }} />
        </span>
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-400">{label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums text-zinc-100">{value}</span>
        {change != null && (
          <span
            className={`flex items-center gap-0.5 text-xs font-medium ${
              up ? "text-emerald-400" : down ? "text-red-400" : "text-zinc-500"
            }`}
          >
            {up && <TrendingUp className="h-3 w-3" />}
            {down && <TrendingDown className="h-3 w-3" />}
            {change > 0 ? "+" : ""}
            {change}%
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

/**
 * Ranked list with an inline magnitude bar — the right form for "top N by
 * count". The number is always present as text too, so the bar is a bonus
 * rather than the only encoding. `accent` tints the bars so each panel has
 * its own identity; `dotFor` adds a small coloured chip per row (used for
 * per-channel colouring on the referrer list).
 */
export function TopList({
  title,
  icon: Icon,
  rows,
  empty,
  accent,
  format,
  dotFor,
}: {
  title: string;
  icon?: ComponentType<{ className?: string }>;
  rows: Count[];
  empty: string;
  accent?: string;
  format?: (r: Count) => string;
  dotFor?: (r: Count) => string | null;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.views), 0) || 1;
  // HTML inline style, so the no-accent fallback can track the theme via the
  // zinc variable (accents are fixed mid-tone hues that work on both).
  const barColor = accent ? `${accent}26` : "color-mix(in oklab, var(--color-zinc-700) 70%, transparent)";
  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900 p-4">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-zinc-200">
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" />}
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-500">{empty}</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {rows.map((r) => {
            const dot = dotFor ? dotFor(r) : null;
            return (
              <li key={r.label} className="relative">
                <div
                  className="absolute inset-y-0 left-0 rounded-md"
                  style={{ width: `${(r.views / max) * 100}%`, backgroundColor: barColor }}
                  aria-hidden
                />
                <div className="relative flex items-center justify-between gap-3 px-2 py-1.5">
                  <span className="flex min-w-0 items-center gap-2 text-sm text-zinc-300">
                    {dot && (
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: dot }}
                        aria-hidden
                      />
                    )}
                    <span className="truncate">{format ? format(r) : r.label}</span>
                  </span>
                  <span className="shrink-0 text-sm tabular-nums text-zinc-400">
                    {r.views.toLocaleString()}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
